/**
 * Partner status and KYC state constants
 * These values match the database schema and GraphQL types
 */

/**
 * Partner status mapping
 * Maps database status values to display labels and colors
 *
 * @example
 * ```ts
 * const status = getPartnerStatus('active');
 * // Returns: { label: 'Active', color: 'bg-green-500' }
 * ```
 */
export const PARTNER_STATUS_MAP = {
  pending: { label: 'Pending', color: 'bg-yellow-500' },
  active: { label: 'Active', color: 'bg-green-500' },
  suspended: { label: 'Suspended', color: 'bg-red-500' },
} as const;

/**
 * Partner KYC state mapping
 * Maps database KYC state values to display labels and colors
 *
 * @example
 * ```ts
 * const kyc = getPartnerKycState('verified');
 * // Returns: { label: 'Verified', color: 'bg-green-500' }
 * ```
 */
export const PARTNER_KYC_STATE_MAP = {
  unverified: { label: 'Unverified', color: 'bg-gray-500' },
  in_review: { label: 'In Review', color: 'bg-yellow-500' },
  verified: { label: 'Verified', color: 'bg-green-500' },
  rejected: { label: 'Rejected', color: 'bg-red-500' },
} as const;

export type PartnerStatus = keyof typeof PARTNER_STATUS_MAP;
export type PartnerKycState = keyof typeof PARTNER_KYC_STATE_MAP;

/**
 * Status configuration type
 */
export type StatusConfig =
  (typeof PARTNER_STATUS_MAP)[keyof typeof PARTNER_STATUS_MAP];

/**
 * KYC state configuration type
 */
export type KycStateConfig =
  (typeof PARTNER_KYC_STATE_MAP)[keyof typeof PARTNER_KYC_STATE_MAP];

/**
 * Get partner status configuration
 * Returns the status configuration for a given status string, or defaults to 'pending'
 *
 * @param status - The partner status string from the database/GraphQL
 * @returns Status configuration object with label and color
 *
 * @example
 * ```ts
 * const status = getPartnerStatus('active');
 * // Returns: { label: 'Active', color: 'bg-green-500' }
 * ```
 */
export const getPartnerStatus = (status: string): StatusConfig => {
  return (
    PARTNER_STATUS_MAP[status as keyof typeof PARTNER_STATUS_MAP] ||
    PARTNER_STATUS_MAP.pending
  );
};

/**
 * Get partner KYC state configuration
 * Returns the KYC state configuration for a given KYC state string, or defaults to 'unverified'
 *
 * @param kycState - The partner KYC state string from the database/GraphQL
 * @returns KYC state configuration object with label and color
 *
 * @example
 * ```ts
 * const kyc = getPartnerKycState('verified');
 * // Returns: { label: 'Verified', color: 'bg-green-500' }
 * ```
 */
export const getPartnerKycState = (kycState: string): KycStateConfig => {
  return (
    PARTNER_KYC_STATE_MAP[kycState as keyof typeof PARTNER_KYC_STATE_MAP] ||
    PARTNER_KYC_STATE_MAP.unverified
  );
};

/**
 * Type guard to check if a string is a valid partner status
 *
 * @param status - The string to validate
 * @returns True if the string is a valid partner status
 *
 * @example
 * ```ts
 * if (isValidPartnerStatus('active')) {
 *   // TypeScript knows status is PartnerStatus here
 * }
 * ```
 */
export const isValidPartnerStatus = (
  status: string,
): status is PartnerStatus => {
  return status in PARTNER_STATUS_MAP;
};

/**
 * Type guard to check if a string is a valid partner KYC state
 *
 * @param kycState - The string to validate
 * @returns True if the string is a valid partner KYC state
 *
 * @example
 * ```ts
 * if (isValidPartnerKycState('verified')) {
 *   // TypeScript knows kycState is PartnerKycState here
 * }
 * ```
 */
export const isValidPartnerKycState = (
  kycState: string,
): kycState is PartnerKycState => {
  return kycState in PARTNER_KYC_STATE_MAP;
};

/**
 * Array of all valid partner statuses
 * Useful for form selects or iteration
 */
export const PARTNER_STATUSES = Object.keys(
  PARTNER_STATUS_MAP,
) as PartnerStatus[];

/**
 * Array of all valid partner KYC states
 * Useful for form selects or iteration
 */
export const PARTNER_KYC_STATES = Object.keys(
  PARTNER_KYC_STATE_MAP,
) as PartnerKycState[];

/**
 * Partner status display order
 * Defines the order in which statuses should be displayed in UI
 */
export const PARTNER_STATUS_ORDER: PartnerStatus[] = [
  'pending',
  'active',
  'suspended',
];

/**
 * Partner KYC state display order
 * Defines the order in which KYC states should be displayed in UI
 */
export const PARTNER_KYC_STATE_ORDER: PartnerKycState[] = [
  'unverified',
  'in_review',
  'verified',
  'rejected',
];
